import React, { FC } from 'react';
import { View } from 'react-native';
import { isTabletOrWeb, SCREEN_PADDING_HORIZONTAL } from '@helpers';
import { useRoute } from '@react-navigation/native';
import { useManageNcFileVersions } from '@components/ncFileVersions/hooks/useManageNcFileVersions';
import { connect } from 'react-redux';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { NCFileSearchItem } from '@interfaces/search';
import ColorWrapper from '@styles/colors';
import { ColorFunction } from '@styles/colors/types';
import NCFileVersionDetailsModeSelectors from './NCFileVersionDetailsModeSelectors';
import CentralColumn from './CentralColumn';
import { RightColumnLevel } from '@components/ncFileVersions/types';
import RightColumn from './RightColumn';
import { Machine } from '@interfaces/machines';
import { notchTop, scale } from '@components/frame';
import BackButton from '@components/common/backButton';
import { PHONE_DETAILS_HEADER_MARGIN_TOP } from '@v2/pages/common/constants/base.constants';

interface NCFileVersionDetailsProps {
  ncFileData: ReducerEntityWithLoadingState<NCFileSearchItem | null>;
  C: ColorFunction;
  selectedItem: Machine;
}

const NCFileVersionDetails: FC<NCFileVersionDetailsProps> = ({ ncFileData, C, selectedItem }) => {
  const route = useRoute();

  const showOnlyActiveVersionsInitialState = route.params?.showOnlyActiveVersionsInitialState;
  const ncFileVersionIdToSelect = route.params?.ncFileVersionId;

  const ncFile = ncFileData.data;

  const {
    favoriteOrLatest,
    selectedVersion,
    rightColumnLevel,
    setRightColumnLevel,
    onSendToMachine,
    onMarkThisVersionAsInactive,
    enterComparisonMode,
  } = useManageNcFileVersions({
    ncFile,
    shouldLoadNcFile: false,
    showOnlyActiveVersionsInitialState,
    ncFileVersionIdToSelect: ncFileVersionIdToSelect != null ? +ncFileVersionIdToSelect : undefined,
    C,
  });

  const totalVersionsCount = ncFile?.versions?.length ?? 0;
  const hasMultipleVersions = totalVersionsCount > 1;

  if (isTabletOrWeb) {
    return null;
  }

  return (
    <View style={{ paddingTop: scale(PHONE_DETAILS_HEADER_MARGIN_TOP + notchTop), flex: 1, backgroundColor: C('Background/Light/Detail') }}>
      <View style={{ marginBottom: scale(7) }}>
        <BackButton text="Versions" />
      </View>
      <View
        style={[
          { flex: 1, gap: scale(18), alignItems: 'center' },
          rightColumnLevel !== RightColumnLevel.Code && { paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL) },
        ]}
      >
        <NCFileVersionDetailsModeSelectors {...{ rightColumnLevel, setRightColumnLevel }} />
        {ncFile && (
          <>
            {selectedVersion && favoriteOrLatest && (
              <CentralColumn
                C={C}
                selectedVersion={selectedVersion}
                ncFile={ncFile}
                visible={rightColumnLevel === RightColumnLevel.Info}
                onSendToMachine={onSendToMachine}
                onMarkThisVersionAsInactive={onMarkThisVersionAsInactive}
                onEnterComparisonMode={enterComparisonMode}
                hasMultipleVersions={hasMultipleVersions}
              />
            )}
            <RightColumn selectedItem={selectedItem} C={C} ncFile={ncFile} visible={rightColumnLevel === RightColumnLevel.Code} selectedVersion={selectedVersion} />
          </>
        )}
      </View>
    </View>
  );
};

export default connect(
  ({ details: { ncFileData }, machines: { selectedItem } }) => ({ ncFileData, selectedItem }),
  {}
)(ColorWrapper(NCFileVersionDetails));
